import { Component, ElementRef, OnDestroy, OnInit, ViewChild, computed, inject, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { ApiService } from './api.service';
import { I18nService, TranslatePipe } from './i18n.service';

interface Material {
  id: number | string;
  name: string;
  serial_no?: string;
  description?: string;
  photo_url?: string;
  unit?: string;
  max_sell_price?: number | string | null;
  cost_price?: number | string | null;
  is_active?: number | string | null;
  parent_id?: number | string | null;
  sub_item_count?: number | string;
  created_at?: string;
  updated_at?: string;
}

@Component({
  selector: 'app-inventory',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslatePipe],
  template: `
    <div class="pb-24 lg:pb-8 min-h-screen lg:min-h-[calc(100vh-72px)] flex flex-col" style="background:#F7FBFD;">
      <header class="px-5 pt-4 pb-4 sticky top-0 z-30 text-white shadow-sm flex items-center gap-3 lg:hidden"
              style="background:linear-gradient(135deg,#0F334A 0%,#145B7A 100%);">
        @if (selectedParent()) {
          <button (click)="goBack()" class="w-10 h-10 rounded-2xl bg-white/10 border border-white/15 flex items-center justify-center text-white/90 active:bg-white/15 shrink-0">
            <i class="ti ti-arrow-left text-[20px]"></i>
          </button>
        }
        <div class="min-w-0 flex-1">
          <p class="text-[10px] font-medium text-white/55 uppercase tracking-widest">
            {{ selectedParent() ? 'Category' : ('inventory' | t) }}
          </p>
          <h1 class="text-lg font-semibold text-white/95 truncate">
            {{ selectedParent() ? selectedParent()!.name : ('materials' | t) }}
          </h1>
        </div>
        <span class="text-[10px] font-medium px-2.5 py-1 rounded-full uppercase border bg-white/10 text-white/75 border-white/15">
          {{ total() | number }}
        </span>
      </header>

      <main class="px-4 pt-5 flex-1 space-y-4 lg:px-7 lg:pt-6">
        @if (selectedParent()) {
          <button type="button" (click)="goBack()" class="hidden lg:inline-flex min-h-11 items-center text-sm text-sky-800">Back to materials</button>
          <section class="bg-sky-50 border border-sky-100 rounded-2xl px-4 py-3 flex items-center gap-3">
            <div class="w-9 h-9 rounded-xl bg-white text-[#1A6FA6] border border-sky-100 flex items-center justify-center shrink-0">
              <i class="ti ti-category text-lg"></i>
            </div>
            <div class="min-w-0">
              <p class="text-[10px] font-semibold text-sky-500 uppercase tracking-wider">Browsing</p>
              <p class="text-sm font-semibold text-sky-800 truncate">{{ selectedParent()!.name }}</p>
            </div>
          </section>
        }

        <section class="relative">
          <i class="ti ti-search absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-lg"></i>
          <input type="text"
                 aria-label="Search materials by name or product code" placeholder="Search name or product code"
                 [(ngModel)]="searchQuery"
                 (input)="onSearch()"
                 class="w-full h-11 pl-11 pr-11 bg-white border border-slate-100 rounded-2xl font-normal focus:outline-none focus:border-[#1A6FA6] focus:ring-2 focus:ring-sky-100 transition-all text-sm text-slate-800">
          @if (searchQuery.trim()) {
            <button aria-label="Clear search" (click)="clearSearch()" class="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-xl bg-slate-50 text-slate-400 flex items-center justify-center active:bg-slate-100">
              <i class="ti ti-x"></i>
            </button>
          }
        </section>

        @if (loadError()) {
          <section class="bg-rose-50 border border-rose-100 rounded-2xl p-4 flex items-start gap-3">
            <i class="ti ti-alert-circle text-rose-500 text-xl shrink-0"></i>
            <div class="flex-1">
              <p class="text-sm font-semibold text-rose-800">Unable to load materials</p>
              <p class="text-xs text-rose-600 mt-0.5">{{ loadError() }}</p>
            </div>
            <button (click)="loadMaterials()" class="text-xs font-semibold text-rose-700 bg-white/70 border border-rose-100 rounded-lg px-3 py-1.5">
              {{ 'retry' | t }}
            </button>
          </section>
        }

        @if (isLoading()) {
          <section class="space-y-3">
            @for (n of [1,2,3,4,5]; track n) {
              <div class="bg-white rounded-2xl p-4 flex gap-3 items-center animate-pulse border border-slate-100">
                <div class="w-12 h-12 rounded-xl bg-slate-100 shrink-0"></div>
                <div class="flex-1 space-y-2">
                  <div class="h-3 bg-slate-100 rounded-full w-1/3"></div>
                  <div class="h-4 bg-slate-100 rounded-full w-3/4"></div>
                  <div class="h-3 bg-slate-100 rounded-full w-1/2"></div>
                </div>
              </div>
            }
          </section>
        } @else {
          <section>
            <div class="flex items-center justify-between mb-2 px-1">
              <p class="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">{{ listTitle() }}</p>
              @if (searchQuery.trim()) {
                <span class="text-[11px] font-medium text-slate-400">{{ searchQuery.trim() }}</span>
              }
            </div>

            <p class="text-xs text-slate-500 mb-3" aria-live="polite">Showing {{ materials().length ? (page() - 1) * pageSize + 1 : 0 }}&ndash;{{ (page() - 1) * pageSize + materials().length }} of {{ total() | number }} materials</p>
            @if (materials().length === 0) {
              <div class="flex flex-col items-center justify-center py-16 text-center bg-white rounded-2xl border border-slate-100">
                <div class="w-14 h-14 rounded-2xl bg-slate-50 border border-slate-100 flex items-center justify-center mb-4">
                  <i class="ti ti-package-off text-2xl text-slate-300"></i>
                </div>
                <h3 class="font-semibold text-slate-700 text-base mb-1">{{ 'noItemsFound' | t }}</h3>
                <p class="text-slate-400 text-sm max-w-xs leading-relaxed">Try a different material name or serial number.</p>
              </div>
            } @else {
              <div class="grid grid-cols-1 gap-1.5 lg:grid-cols-2 xl:grid-cols-3">
                @for (item of materials(); track item.id) {
                  <button type="button" (click)="onItemClick(item)" aria-haspopup="dialog"
                          [attr.aria-label]="'View details for ' + item.name"
                          class="w-full min-h-14 bg-white rounded-lg px-3 py-2 border border-slate-200 flex items-center gap-2 text-left transition hover:border-sky-300 focus-visible:outline-2 focus-visible:outline-sky-600">
                    <div class="min-w-0 flex-1">
                      <h3 class="truncate font-semibold text-sm leading-5 text-slate-800">{{ item.name }}</h3>
                      <p class="truncate text-[11px] leading-4 text-slate-500">{{ item.serial_no || 'No code' }} &middot; {{ item.unit || 'No unit' }}</p>
                    </div>
                    <div class="shrink-0 text-right">
                      @if (hasChildren(item)) {
                        <span class="text-xs text-sky-700">{{ item.sub_item_count }} types</span>
                      } @else {
                        <span class="text-xs font-semibold text-emerald-700">{{ price(item.max_sell_price) }}</span>
                      }
                    </div>
                    <i class="ti ti-chevron-right text-sm text-slate-400 shrink-0" aria-hidden="true"></i>
                  </button>
                }
              </div>
            }
            @if (totalPages() > 1) {
              <nav aria-label="Materials pages" class="flex items-center justify-between gap-3 mt-5">
                <button type="button" (click)="changePage(-1)" [disabled]="page() <= 1" class="min-h-11 px-4 rounded-xl border bg-white disabled:opacity-40">Previous</button>
                <span class="text-xs text-slate-600">Page {{ page() }} of {{ totalPages() | number }}</span>
                <button type="button" (click)="changePage(1)" [disabled]="page() >= totalPages()" class="min-h-11 px-4 rounded-xl border bg-white disabled:opacity-40">Next</button>
              </nav>
            }
          </section>
        }
      </main>
    </div>
    <dialog #detailDialog aria-labelledby="material-detail-title" tabindex="-1" (keydown.escape)="detailDialog.close()" (click)="onDialogClick($event)" (close)="selectedItem.set(null)"
            class="material-dialog rounded-3xl border border-slate-200 p-0 shadow-2xl text-slate-800">
      @if (selectedItem(); as item) {
        <div class="sticky top-0 bg-white border-b border-slate-100 px-5 py-3 flex items-center justify-between gap-3">
          <h2 id="material-detail-title" class="font-semibold">Material details</h2>
          <button type="button" aria-label="Close material details" (click)="detailDialog.close()" class="w-11 h-11 rounded-xl bg-slate-100 text-xl">&times;</button>
        </div>
        <div class="p-5 space-y-5">
          <img [src]="item.photo_url || defaultPhoto" (error)="onImageError($event)" [alt]="item.name" class="w-full h-44 object-contain rounded-2xl bg-slate-50">
          <h3 class="text-lg font-semibold break-words">{{ item.name }}</h3>
          <p class="whitespace-pre-wrap break-words text-sm leading-relaxed text-slate-600">{{ item.description || 'No description provided' }}</p>
          <dl class="grid grid-cols-2 gap-4 text-sm">
            @for (field of detailFields(item); track field.label) {
              <div class="min-w-0"><dt class="text-xs text-slate-500">{{ field.label }}</dt><dd class="mt-1 font-medium break-words whitespace-pre-wrap">{{ field.value }}</dd></div>
            }
          </dl>
          @if (hasChildren(item)) {
            <button type="button" (click)="browseChildren(item)" class="w-full min-h-12 rounded-xl bg-sky-800 text-white font-semibold">Browse {{ item.sub_item_count }} sub-items</button>
          }
        </div>
      }
    </dialog>
  `,
  styles: [`
    .material-dialog { margin: auto; width: min(40rem, calc(100vw - 2rem)); max-height: 85dvh; overflow-y: auto; overscroll-behavior: contain; }
    .material-dialog::backdrop { background: rgb(15 23 42 / 60%); }
  `]
})
export class InventoryComponent implements OnInit, OnDestroy {
  api = inject(ApiService);
  i18n = inject(I18nService);

  @ViewChild('detailDialog') detailDialog!: ElementRef<HTMLDialogElement>;
  selectedItem = signal<Material | null>(null);
  page = signal(1);
  total = signal(0);
  totalPages = signal(0);
  readonly pageSize = 50;
  private request?: Subscription;
  private parentHistory: (Material | null)[] = [];
  materials = signal<Material[]>([]);
  isLoading = signal(true);
  loadError = signal('');
  selectedParent = signal<Material | null>(null);
  searchQuery = '';
  defaultPhoto = 'https://nzplumbinghq.co.nz/api/assets/default_mat.jpeg';

  private searchTimer: ReturnType<typeof setTimeout> | undefined;

  listTitle = computed(() => {
    if (this.searchQuery.trim()) return this.i18n.translate('search');
    if (this.selectedParent()) return this.i18n.translate('materials');
    return this.i18n.translate('materials');
  });

  ngOnInit() {
    this.loadMaterials();
  }

  ngOnDestroy() {
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.request?.unsubscribe();
    this.detailDialog.nativeElement.close();
  }

  loadMaterials() {
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.request?.unsubscribe();
    this.isLoading.set(true);
    this.loadError.set('');

    const term = this.searchQuery.trim();
    const params: Parameters<ApiService['getMaterials']>[0] = {
      limit: this.pageSize,
      page: this.page(),
      include_children: false,
    };
    if (term) params.search = term;
    if (this.selectedParent()) params.parent_id = Number(this.selectedParent()!.id);

    this.request = this.api.getMaterials(params).subscribe({
      next: (res) => {
        this.isLoading.set(false);
        if (res.success) {
          this.materials.set(res.data?.materials || []);
          this.total.set(Number(res.data?.pagination?.total ?? this.materials().length));
          this.totalPages.set(Number(res.data?.pagination?.total_pages ?? 1));
        } else {
          this.materials.set([]);
          this.total.set(0);
          this.totalPages.set(0);
          this.loadError.set(res.message || 'Materials could not be loaded.');
        }
      },
      error: (err: HttpErrorResponse) => {
        this.isLoading.set(false);
        this.materials.set([]);
        this.total.set(0);
        this.totalPages.set(0);
        this.loadError.set(err?.error?.message || 'Materials could not be loaded.');
      }
    });
  }

  onSearch() {
    this.request?.unsubscribe();
    this.isLoading.set(true);
    this.page.set(1);
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => this.loadMaterials(), 300);
  }

  clearSearch() {
    this.page.set(1);
    this.searchQuery = '';
    this.loadMaterials();
  }

  onItemClick(item: Material) {
    this.selectedItem.set(item);
    this.detailDialog.nativeElement.showModal();
  }

  onDialogClick(event: MouseEvent) {
    if (event.target !== this.detailDialog.nativeElement) return;
    const bounds = this.detailDialog.nativeElement.getBoundingClientRect();
    if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) {
      this.detailDialog.nativeElement.close();
    }
  }

  price(value: unknown): string {
    return value === null || value === undefined || value === '' || !Number.isFinite(Number(value))
      ? 'Not provided' : new Intl.NumberFormat('en-NZ', { style: 'currency', currency: 'NZD' }).format(Number(value));
  }

  status(item: Material): string {
    return item.is_active === null || item.is_active === undefined ? 'Status not provided' : Number(item.is_active) === 1 ? 'Active' : 'Inactive';
  }

  detailFields(item: Material) {
    return [
      { label: 'Product code', value: item.serial_no || 'Not provided' },
      { label: 'Unit of measure', value: item.unit || 'Not provided' },
      { label: 'Unit selling price (NZD)', value: this.price(item.max_sell_price) },
      { label: 'Cost price (NZD)', value: this.price(item.cost_price) },
      { label: 'Status', value: this.status(item) },
      { label: 'Material ID', value: item.id },
      { label: 'Parent material ID', value: item.parent_id ?? 'Top-level material' },
      { label: 'Sub-items', value: item.sub_item_count ?? 0 },
      { label: 'Created', value: item.created_at || 'Not provided' },
      { label: 'Last updated', value: item.updated_at || 'Not provided' },
    ];
  }

  changePage(direction: number) {
    const next = this.page() + direction;
    if (next < 1 || next > this.totalPages()) return;
    this.page.set(next);
    this.loadMaterials();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  browseChildren(item: Material) {
    this.detailDialog.nativeElement.close();
    this.parentHistory.push(this.selectedParent());
    this.page.set(1);
    this.selectedParent.set(item);
    this.searchQuery = '';
    this.loadMaterials();
  }

  goBack() {
    this.selectedParent.set(this.parentHistory.pop() ?? null);
    this.page.set(1);
    this.searchQuery = '';
    this.loadMaterials();
  }

  hasChildren(item: Material): boolean {
    return Number(item?.sub_item_count || 0) > 0;
  }

  onImageError(event: Event) {
    const img = event.target as HTMLImageElement;
    if (img.src !== this.defaultPhoto) {
      img.src = this.defaultPhoto;
    }
  }
}
